import React, { useState, useEffect, useRef } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { membersAPI } from '../../services/api';
import './MemberQRScanner.css';

const MemberQRScanner = ({ onMemberFound }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scannedMember, setScannedMember] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.clear();
      } catch (err) {
        console.error('Failed to stop scanner:', err);
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
    const reader = document.getElementById('member-qr-reader');
    if (reader) {
      reader.innerHTML = '';
    }
  };

  const startScanner = async () => {
    setScanError('');
    setScannedMember(null);

    try {
      const html5QrcodeScanner = new Html5QrcodeScanner(
        'member-qr-reader',
        {
          fps: 10,
          qrbox: { width: 220, height: 220 },
          aspectRatio: 1.0,
        },
        false
      );

      html5QrcodeScanner.render(onScanSuccess, () => {});
      scannerRef.current = html5QrcodeScanner;
      setIsScanning(true);
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setScanError('無法啟動相機，請確認已授予相機權限');
    }
  };

  const onScanSuccess = async (decodedText) => {
    if (lookupLoading) return;

    setLookupLoading(true);
    setScanError('');

    try {
      const response = await membersAPI.lookup(decodedText);
      const member = response.data?.match === 'one' ? response.data.results[0] : null;
      if (!member) {
        setScanError(response.data?.match === 'many' ? '多於一位會員，請改用完整編號' : '找不到此QR碼對應的會員');
        return;
      }
      setScannedMember(member);
      onMemberFound?.(member);
      await stopScanner();

      if (navigator.vibrate) {
        navigator.vibrate(200);
      }
    } catch (err) {
      setScanError(err.response?.data?.error || '找不到此QR碼對應的會員');
    } finally {
      setLookupLoading(false);
    }
  };

  const clearResult = () => {
    setScannedMember(null);
    setScanError('');
  };

  return (
    <div className="member-qr-scanner card">
      <div className="member-qr-scanner-header">
        <h2>QR掃描器</h2>
        <p>掃描會員QR碼以立即查看會員資料</p>
      </div>

      <div className="member-qr-scanner-controls">
        {!isScanning ? (
          <button type="button" onClick={startScanner} className="btn btn-primary" disabled={lookupLoading}>
            {lookupLoading ? '查詢中...' : '開始掃描'}
          </button>
        ) : (
          <button type="button" onClick={stopScanner} className="btn btn-secondary">
            停止掃描
          </button>
        )}
        {scannedMember && (
          <button type="button" onClick={clearResult} className="btn btn-outline">
            清除結果
          </button>
        )}
      </div>

      {scanError && <div className="alert alert-error">{scanError}</div>}

      {scannedMember && (
        <div className="member-qr-result alert alert-success">
          <h3>已找到會員</h3>
          <div className="member-qr-result-grid">
            <p><strong>中文姓名：</strong>{scannedMember.chinese_name}</p>
            <p><strong>英文姓名：</strong>{scannedMember.english_name || '-'}</p>
            <p><strong>會員編號：</strong>{scannedMember.member_number}</p>
            <p><strong>電話：</strong>{scannedMember.phone_number || '-'}</p>
            <p><strong>Email：</strong>{scannedMember.email || '-'}</p>
            <p><strong>地區：</strong>{scannedMember.district || '-'}</p>
            <p>
              <strong>狀態：</strong>
              <span className={`status-badge ${scannedMember.is_active ? 'status-active' : 'status-inactive'}`}>
                {scannedMember.is_active ? '啟用中' : '已停用'}
              </span>
            </p>
          </div>
        </div>
      )}

      <div id="member-qr-reader" className="member-qr-reader" />
    </div>
  );
};

export default MemberQRScanner;
